import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../utils/api'
import Alert from '../ui/Alert'
import ar from '../../locales/ar.json'

const ta = ar.app

const STATUS_LABELS = {
  draft:            'مسودة',
  submitted:        'مقدَّم',
  'pending review': 'قيد المراجعة',
  documented:       'موثَّق',
  rejected:         'مرفوض',
}
const STATUS_BADGE = {
  draft:            'badge-stone',
  submitted:        'badge-blue',
  'pending review': 'badge-yellow',
  documented:       'badge-green',
  rejected:         'badge-red',
}

function StatCard({ label, value, className = '' }) {
  return (
    <div className={`card text-center ${className}`}>
      <p className="text-2xl font-bold text-primary-700">{value ?? '—'}</p>
      <p className="text-xs text-stone-500 mt-1">{label}</p>
    </div>
  )
}

export default function RegionDashboard({ regionId }) {
  const [data,    setData]    = useState(null)
  const [cases,   setCases]   = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState('')

  useEffect(() => {
    if (!regionId) return
    setLoading(true)
    Promise.all([
      api.get(`admin/regions/${regionId}/stats`),
      api.get('admin/cases', { params: { region: regionId, limit: 50, page: 1 } }),
    ])
      .then(([stats, casesRes]) => {
        setData(stats.data)
        setCases(casesRes.data.cases || [])
      })
      .catch((err) => setError(err.message || ta.error))
      .finally(() => setLoading(false))
  }, [regionId])

  if (loading) return <div className="card text-center py-12 text-stone-400">{ta.loading}</div>
  if (error)   return <Alert type="error" message={error} />
  if (!data)   return null

  return (
    <div>
      <h2 className="text-lg font-bold text-stone-800 mb-4">{data.name}</h2>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="إجمالي الحالات"  value={data.total} />
        <StatCard label="موثَّق"           value={data.documented}  className="border-l-2 border-green-400"  />
        <StatCard label="قيد المراجعة"     value={data.review}      className="border-l-2 border-yellow-400" />
        <StatCard label="مرفوض"          value={data.rejected}    className="border-l-2 border-red-400" />
      </div>

      {/* Sub-region breakdown */}
      {data.sub_regions?.length > 0 && (
        <div className="card mb-6">
          <h3 className="font-semibold text-stone-700 mb-3 text-sm">توزيع حسب الحي</h3>
          <div className="space-y-2">
            {data.sub_regions.map((sr) => (
              <div key={sr.name} className="flex items-center justify-between text-sm">
                <span className="text-stone-700">{sr.name}</span>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-stone-100 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full"
                      style={{ width: `${Math.min(100, (sr.count / (data.total || 1)) * 100)}%` }}
                    />
                  </div>
                  <span className="text-stone-500 w-8 text-left">{sr.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cases table */}
      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm text-right">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="px-4 py-3 font-semibold text-stone-600">الرقم</th>
              <th className="px-4 py-3 font-semibold text-stone-600">اسم الشهيد</th>
              <th className="px-4 py-3 font-semibold text-stone-600">الحالة</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {cases.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-8 text-stone-400">لا توجد حالات</td></tr>
            ) : cases.map((c) => (
              <tr key={c.id} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="px-4 py-3 font-mono text-xs text-stone-500">{c.readable_id}</td>
                <td className="px-4 py-3 font-medium text-stone-800">{c.martyr_fullname || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`badge ${STATUS_BADGE[c.status] || 'badge-stone'}`}>
                    {STATUS_LABELS[c.status] || c.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <Link to={`/admin/cases/${c.id}`} className="text-xs text-primary-600 hover:text-primary-800">
                    عرض
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
